import { useMemo, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { Badge, Card, Group, Input, Select, Stack, Text } from "@/components/ui";
import { ModulosDeUsuarioSkeleton } from "./ModulosSkeleton";
import { cn } from "@/lib/cn";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { UsuarioAdmin } from "@/lib/types";
import { useUsuariosAdmin } from "@/features/admin-usuarios/hooks/use-usuarios-admin";
import { useEstablecerModuloUsuario, useModulosDeUsuario } from "../hooks/use-modulos";
import type { EstadoModulo, ModuloDeUsuario } from "../services/modulos-service";

/**
 * Vista POR USUARIO del gestor de módulos: elige un usuario y, por cada módulo, decide si lo
 * HEREDA de sus roles, se le OTORGA extra o se le BLOQUEA (el bloqueo gana). Muestra el efecto
 * resultante. El backend aplica al instante; la UI del usuario, tras su próximo inicio de sesión.
 *
 * Hotspot 89 · la búsqueda de usuarios es SERVER-SIDE (antes se traía solo la primera página de
 * 100 y el buscador filtraba sobre esas): se escribe el correo o nombre y se eligen de los
 * resultados (hasta 20 por búsqueda). El listado se lee con el permiso del gestor (admin.modulos).
 */
const RESULTADOS_BUSQUEDA = 20;

export function ModulosPorUsuarioPanel() {
  const [busqueda, setBusqueda] = useState("");
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);
  const { data: pagina, isFetching } = useUsuariosAdmin({
    busqueda: busquedaDebounced.trim() || undefined,
    page: 1,
    pageSize: RESULTADOS_BUSQUEDA,
  });
  const resultados = pagina?.items ?? [];
  const [usuario, setUsuario] = useState<UsuarioAdmin | undefined>(undefined);
  const usuarioId = usuario?.id ?? "";
  // El elegido se conserva aunque la búsqueda cambie y deje de aparecer en los resultados.
  const opciones = usuario && !resultados.some((u) => u.id === usuario.id) ? [usuario, ...resultados] : resultados;

  const { data: modulos, isLoading } = useModulosDeUsuario(usuarioId);
  const establecer = useEstablecerModuloUsuario(usuarioId);

  const grupos = useMemo(() => agrupar(modulos ?? []), [modulos]);

  return (
    <Stack gap="md">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <Input
          label="Buscar usuario"
          placeholder="Correo o nombre…"
          leftSection={<IconSearch size={16} />}
          value={busqueda}
          onChange={(e) => setBusqueda(e.currentTarget.value)}
          wrapperClassName="w-72"
        />
        <Select
          label="Usuario"
          wrapperClassName="w-96"
          placeholder={isFetching ? "Buscando…" : "Elige de los resultados"}
          options={opciones.map((u) => ({ value: u.id, label: u.fullName ? `${u.fullName} · ${u.email}` : u.email }))}
          value={usuarioId}
          onValueChange={(id) => setUsuario(opciones.find((u) => u.id === id))}
        />
      </Group>
      {pagina && pagina.total > resultados.length ? (
        <Text size="xs" c="dimmed">
          Mostrando {resultados.length} de {pagina.total} usuarios · afina la búsqueda para encontrar a los demás.
        </Text>
      ) : null}

      {!usuarioId ? (
        <Text size="sm" c="dimmed">
          Elige un usuario para ver y ajustar sus módulos.
        </Text>
      ) : isLoading ? (
        <ModulosDeUsuarioSkeleton />
      ) : (
        <>
          <div>
            <Group gap="xs" align="center">
              <Text size="sm" fw={600}>
                {usuario?.fullName || usuario?.email}
              </Text>
              {(usuario?.roles ?? []).map((r) => (
                <Badge key={r} size="sm" color="slate" variant="light">
                  {r}
                </Badge>
              ))}
            </Group>
            <Text size="xs" c="dimmed" className="mt-1">
              «Heredar» toma el módulo de sus roles · «Otorgar» lo da aunque el rol no lo tenga · «Bloquear» lo quita aunque el rol sí lo dé.
            </Text>
          </div>

          <Card padding="lg">
            <Stack gap="lg">
              {grupos.map(([grupo, items]) => (
                <Stack key={grupo} gap={8}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                    {grupo}
                  </Text>
                  <Stack gap={4}>
                    {items.map((m) => (
                      <ModuloRow
                        key={m.codigo}
                        modulo={m}
                        saving={establecer.isPending}
                        onEstado={(estado) => establecer.mutate({ codigo: m.codigo, estado })}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

function ModuloRow({
  modulo,
  saving,
  onEstado,
}: {
  modulo: ModuloDeUsuario;
  saving: boolean;
  onEstado: (estado: EstadoModulo) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-muted/40">
      <div className="min-w-0">
        <Text size="sm" className="truncate">
          {modulo.descripcion}
        </Text>
        <Group gap={6} className="mt-0.5">
          <Text size="xs" ff="mono" c="dimmed">
            {modulo.codigo}
          </Text>
          {modulo.heredadoDeRol ? (
            <Badge size="xs" variant="light" color="slate">
              heredado
            </Badge>
          ) : null}
          <Badge size="xs" variant="light" color={modulo.efectivo ? "exito" : "slate"}>
            {modulo.efectivo ? "acceso" : "sin acceso"}
          </Badge>
        </Group>
      </div>
      <TriEstado value={modulo.override} disabled={saving} onChange={onEstado} />
    </div>
  );
}

const OPCIONES: { estado: EstadoModulo; label: string }[] = [
  { estado: "Heredar", label: "Heredar" },
  { estado: "Otorgar", label: "Otorgar" },
  { estado: "Bloquear", label: "Bloquear" },
];

const COLOR_ACTIVO: Record<EstadoModulo, string> = {
  Heredar: "bg-secondary text-secondary-foreground",
  Otorgar: "bg-exito-600 text-white",
  Bloquear: "bg-peligro-600 text-white",
};

/** Control segmentado de 3 estados (Heredar / Otorgar / Bloquear). */
function TriEstado({
  value,
  disabled,
  onChange,
}: {
  value: EstadoModulo;
  disabled?: boolean;
  onChange: (estado: EstadoModulo) => void;
}) {
  return (
    <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-border">
      {OPCIONES.map((o, i) => (
        <button
          key={o.estado}
          type="button"
          disabled={disabled || o.estado === value}
          onClick={() => onChange(o.estado)}
          className={cn(
            "px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-default",
            i > 0 && "border-l border-border",
            o.estado === value ? COLOR_ACTIVO[o.estado] : "bg-card text-muted-foreground hover:bg-muted"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function agrupar(modulos: ModuloDeUsuario[]): Array<[string, ModuloDeUsuario[]]> {
  const mapa = new Map<string, ModuloDeUsuario[]>();
  for (const m of modulos) {
    const lista = mapa.get(m.grupo) ?? [];
    lista.push(m);
    mapa.set(m.grupo, lista);
  }
  return [...mapa.entries()];
}
